import React, { useEffect, useState } from 'react';
import { ChatbotV2, useChatbotController } from '@amzn/aws-tc-chatbot-ui';
import type { ChatbotUIConfig, FeedbackValue, RenderError, WindowMode } from '@amzn/aws-tc-chatbot-ui';

/**
 * Generic, reusable ChatPanel — wraps the shared `ChatbotV2` component for any
 * AWS T&C agent. Connects to an AG-UI streaming endpoint and handles:
 *
 *   - SSE streaming to the agent (via ChatbotV2 + the AG-UI runtime)
 *   - conversationId persistence (sessionStorage) for cross-refresh continuity
 *   - initial message dispatch via the chatbot controller
 *   - feedback callback wiring
 *   - sensible Cloudscape defaults
 *
 * For myClass usage, prefer `<MyClassChatPanel>` which pre-configures this
 * component with the myClass agent name, branding, and disclaimers.
 */

/** Default disclaimer lines used when none are provided. */
const DEFAULT_FOOTER_LINES: readonly string[] = ['AI-generated content may be inaccurate.'];

export interface ChatPanelProps {
    /** Registered agent name in the AI Gateway routing config. REQUIRED. */
    agentName: string;
    /** AG-UI streaming endpoint URL. REQUIRED. */
    endpoint: string;
    /** Custom routing header name forwarded to the gateway with the agent name. */
    agentHeaderName?: string;
    /** Window mode — defaults to "floating" (self-managed launcher button). */
    mode?: WindowMode;
    /** Floating mode only: start with the window open (library default) or,
     *  when false, collapsed to the launcher button until the user clicks it. */
    defaultOpen?: boolean;
    /** Initial message sent as the first turn when the panel mounts. */
    initialMessage?: string;
    /** Fires when the user closes the panel. */
    onClose?: () => void;
    /** Header title shown at the top of the chat panel. */
    headerTitle?: string;
    /** Welcome message shown as the first assistant bubble. */
    welcomeMessage?: string;
    /** Disclaimer lines shown in the footer. */
    footerLines?: readonly string[];
    /** Footer links (e.g. policy / terms). */
    footerLinks?: readonly { label: string; href: string }[];
    /** sessionStorage key for conversationId persistence — pass a unique key per
     *  agent so multiple chats on the same origin don't collide. */
    conversationIdStorageKey?: string;
    /** Render assistant message bodies as markdown (CommonMark + GFM). Defaults
     *  to true — our agents stream markdown text, so parse it instead of
     *  showing raw `**bold**` / list syntax. */
    enableMarkdown?: boolean;
    /** Refresh the auth session before the streaming connection is opened.
     *  Auth to the AI Gateway rides an HttpOnly cookie (JWT). When it has
     *  expired since the last visit, the first request returns UNAUTHORIZED.
     *  The library resolves auth headers once when it builds the streaming
     *  agent (on mount), so we await this here to renew the cookie first.
     *  Failures are swallowed — a refresh error should not block a request
     *  whose cookie may still be valid. */
    onEnsureAuth?: () => Promise<void>;
    /** Optional feedback handler. No-op by default. */
    onFeedback?: (messageId: string, feedback: FeedbackValue, reasonCode?: string, feedbackText?: string) => void;
    /** Custom renderer for failed assistant turns. Return a ReactNode to replace
     *  the default "Unable to complete response" body, or undefined to keep it. */
    renderError?: RenderError;
}

export const ChatPanel = ({
    agentName,
    endpoint,
    agentHeaderName,
    mode = 'floating',
    defaultOpen,
    initialMessage,
    onClose,
    headerTitle = 'Assistant',
    welcomeMessage = 'Hi! How can I help you today?',
    footerLines = DEFAULT_FOOTER_LINES,
    footerLinks = [],
    conversationIdStorageKey,
    enableMarkdown = true,
    onEnsureAuth,
    onFeedback,
    renderError,
}: ChatPanelProps) => {
    // Persist conversationId across page refreshes (when a storage key is provided).
    //
    // sessionStorage, not localStorage, because the two differ on exactly the case
    // that matters here. sessionStorage is scoped to one tab and survives a reload,
    // so refreshing keeps the conversation and a newly opened tab or window starts a
    // fresh one. localStorage is shared across every tab on the origin and never
    // expires, so a new window would silently resume a conversation the learner
    // thought they had left, and the only way to start over would be to clear
    // browser storage.
    const [conversationId, setConversationId] = useState<string | undefined>(() => {
        if (typeof window === 'undefined' || !conversationIdStorageKey) {
            return undefined;
        }
        return sessionStorage.getItem(conversationIdStorageKey) ?? undefined;
    });

    // Controller lets us imperatively send the initial message after mount.
    const chat = useChatbotController();

    // Send the initial message exactly once when the panel first mounts.
    useEffect(() => {
        if (initialMessage && initialMessage.trim().length > 0) {
            chat.sendMessage(initialMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on first mount
    }, []);

    const handleConversationIdChange = (id: string) => {
        setConversationId(id);
        if (!conversationIdStorageKey) {
            return;
        }
        try {
            sessionStorage.setItem(conversationIdStorageKey, id);
        } catch {
            // sessionStorage may be disabled (Safari private mode etc.) — non-fatal.
        }
    };

    // Resolved once when the library builds the streaming agent (on mount).
    // Refresh the auth cookie first (if a refresher was provided), then return
    // the routing header. Skip entirely when neither is needed so the library
    // falls back to its default (cookie-only, credentials: 'include').
    const getAuthHeaders =
        agentHeaderName || onEnsureAuth
            ? async (): Promise<Record<string, string>> => {
                  if (onEnsureAuth) {
                      try {
                          await onEnsureAuth();
                      } catch {
                          // Non-fatal: proceed with the existing cookie, which may still be valid.
                      }
                  }
                  return agentHeaderName ? { [agentHeaderName]: agentName } : {};
              }
            : undefined;

    const config: ChatbotUIConfig = {
        mode,
        defaultOpen,
        welcomeMessage,
        placeholder: 'Ask a question',
        enableFeedback: true,
        header: {
            title: headerTitle,
            showCloseButton: true,
            showMaximizeButton: mode !== 'fullscreen' && mode !== 'inline',
        },
        footer: {
            disclaimerLines: [...footerLines],
            links: [...footerLinks],
        },
    };

    return (
        <ChatbotV2
            endpoint={endpoint}
            agentName={agentName}
            getAuthHeaders={getAuthHeaders}
            config={config}
            enableMarkdown={enableMarkdown}
            controller={chat}
            conversationId={conversationId}
            onConversationIdChange={handleConversationIdChange}
            onFeedback={onFeedback}
            onClose={onClose}
            renderError={renderError}
        />
    );
};
